import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css";
import {
  FieldProps,
  getFieldContainerStyle,
} from "../InputFieldsController/InputFieldsController.utils";
import LabeledInputWrapper from "../InputFieldsController/LabeledInputWrapper";

interface SSNInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
}

const formatSSN = (ssn: string) => {
  const digits = ssn.replace(/\D/g, "").slice(0, 9);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 5);
  const part3 = digits.slice(5, 9);
  return [part1, part2, part3].filter(Boolean).join("-");
};

const maskSSN = (ssn: string) => {
  const digits = ssn.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) {
    return digits; // Don't mask early input
  }
  const part3 = digits.slice(5, 9);
  return `***-**-${part3}`;
};


const SSNInput: React.FC<SSNInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
}) => {
  const {
    id,
    value = "",
    placeholder,
    disabled,
    isValid = true,
    error,
    show = true,
    selected,
    example,
  } = field;

  const rawDigits = value.replace(/\D/g, "").slice(0, 9);
  const containerStyle = getFieldContainerStyle(field);
  const [showFull, setShowFull] = useState(false);
  const [inputBuffer, setInputBuffer] = useState(rawDigits);

  // Sync buffer with value from backend/store
  useEffect(() => {
    const cleaned = value.replace(/\D/g, "").slice(0, 9);
    setInputBuffer(cleaned);
  }, [value]);

  if (!show) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
  
    // Stop at 9 digits
    if (digitsOnly.length > 9) return;
  
    setInputBuffer(digitsOnly);
    handleChange(id, { target: { value: digitsOnly } });
  };
  
  const displayValue = showFull ? formatSSN(inputBuffer) : maskSSN(inputBuffer);

  const handleFocus = () => {
    setShowFull(true); // show real digits while typing
    handleSelect(id);
  };

  return (
    <div style={containerStyle}>
      <LabeledInputWrapper field={field}>
        <div style={{ position: "relative" }}>
          <Form.Control
            id={id}
            maxLength={11}
            type="text"
            inputMode="numeric"
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            onChange={handleInputChange}
            onBlur={() => { 
              handleBlur(id)
              setShowFull(false);
            }}
            onFocus={() => handleFocus()}
            isInvalid={!isValid}
            className={`${CommonStyles.customInput} ${
              selected ? CommonStyles.selectedField : ""
            }`}
          />
          <span
            onClick={() => setShowFull((prev) => !prev)}
            style={{
              position: "absolute",
              top: "50%",
              right: 10,
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showFull ? <Icon.EyeSlash /> : <Icon.Eye />}
          </span>
        </div>
        {!isValid && error && (
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        )}
      </LabeledInputWrapper>

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default SSNInput;
